'use client';

import type React from 'react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Bot, Loader2, Send, User, Sparkles, Info, AlertCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ChatMessage } from '../components/ChatMessages';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSubscription } from '../hooks/useSubscription';
import type { ReferenceResponse, StatusResponseWithId } from '../types/api';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';

const POLLING_INTERVAL = 2000;
const MAX_POLLING_ATTEMPTS = 30; // 1 minute maximum polling time
const MESSAGE_THRESHOLD = 10; // Switch to polling after this many messages

const SUGGESTED_PROMPTS = [
  {
    label: 'Research a topic',
    toolbox: 'research',
    prompt: 'Help me research a topic for my story ...',
  },
  {
    label: 'Create a Character',
    toolbox: 'character',
    prompt:
      "I'm looking to develop a unique character with a mix of unusual flaws and secret talents. Can you help me create a detailed profile that includes their background, explains how these traits impact their relationships and ambitions, and reveals a defining moment from their past that set them on this path?",
  },
  {
    label: 'Create a World',
    toolbox: 'world',
    prompt: 'Help me design a world for my story.',
  },
  {
    label: 'Writers block challenge',
    toolbox: 'writers-block',
    prompt: `I've hit a wall with writer's block. Inspire me with a fun writing challenge!`,
  },
];

// Add this function before the AIChat component to map toolbox values to display names
const getToolboxDisplayName = (toolbox: string): string => {
  const displayNames: Record<string, string> = {
    character: 'Create Builder',
    world: 'World Builder',
    'writers-block': 'Writers block challenge',
  };

  return displayNames[toolbox] || toolbox;
};

// Add a state variable for toolbox
export default function AIChat() {
  const [id, setId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [creditError, setCreditError] = useState<string | null>(null);
  const [creditDelta, setCreditDelta] = useState<number | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [toolbox, setToolbox] = useState<string>('research'); // Default toolbox value
  const { setSubscriptionSpend, setSubscriptionLimit } = useSubscription();
  const { apiCall } = useAuthenticatedApi();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Reset state when navigating to /ai-chat without an ID
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chatId = params.get('id');
    const isNew = params.get('new') === 'true';
    const toolboxParam = params.get('toolbox');

    if (toolboxParam) {
      setToolbox(toolboxParam);
    }

    // If we're at /ai-chat with no ID or with new=true, reset the state
    if ((isNew || !chatId) && location.pathname === '/ai-chat') {
      setId(null);
      setChatMessages([]);
      setCurrentMessage('');
      setIsSendingMessage(false);
      setProcessingMessage('');
      setCreditError(null);
      setCreditDelta(null);
      setIsTyping(false);

      // If we have the new=true parameter, replace the URL with just /ai-chat
      if (isNew) {
        if (toolboxParam) {
          navigate(`/ai-chat?toolbox=${toolboxParam}`, { replace: true });
        } else {
          navigate('/ai-chat', { replace: true });
        }
      }
    } else if (chatId) {
      setId(chatId);
    }
  }, [location.search, location.pathname, navigate]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, scrollToBottom, isTyping]);

  // Fetch existing chat messages if ID exists
  useEffect(() => {
    const fetchChatMessages = async () => {
      if (!id) return;

      try {
        const response = await apiCall<{
          id: string;
          messages: ChatMessage[] | null;
          status?: string;
          creditsConsumed?: number;
          creditLimit?: number;
        }>('/ai/chat', 'GET', { id });

        if (response.status === 'ERROR_LACK_CREDIT') {
          setCreditError(
            `You've exceeded your credit limit. Used: ${response.creditsConsumed}, Limit: ${response.creditLimit}`
          );
        } else {
          setChatMessages(response.messages ?? []);
        }
      } catch (err) {
        console.error('Error fetching chat messages:', err);
        toast({
          title: 'Error',
          description: 'Error fetching chat messages',
          variant: 'destructive',
        });
      }
    };

    fetchChatMessages();
  }, [id, apiCall, toast]);

  // Update URL with ID
  useEffect(() => {
    if (id && !location.search.includes(id)) {
      navigate(`/ai-chat?id=${id}`, { replace: true });
    }
  }, [id, navigate, location.search]);

  const pollChatStatus = useCallback(
    async (reference: string) => {
      let attempts = 0;
      setIsTyping(true);

      const poll = async () => {
        if (attempts >= MAX_POLLING_ATTEMPTS) {
          setIsSendingMessage(false);
          setIsTyping(false);
          setProcessingMessage('Operation timed out. Please try again.');
          // Clear error message after 10 seconds
          setTimeout(() => {
            setProcessingMessage('');
          }, 10000);
          return;
        }

        try {
          const statusData = await apiCall<StatusResponseWithId>(`/status?reference=${reference}`, 'GET');

          if (statusData.status === 'COMPLETE') {
            setSubscriptionSpend(statusData.creditsConsumed);
            setSubscriptionLimit(statusData.creditLimit);
          }

          switch (statusData.status) {
            case 'COMPLETE': {
              // If this is the first message and we get a new ID back, store it
              if (!id && statusData.id) {
                setId(statusData.id);
              }
              // Replace loading message with actual AI response
              setChatMessages((prev) => {
                const allButLast = prev.slice(0, -1);
                return [
                  ...allButLast,
                  {
                    user: false,
                    msg: statusData.aiResponse || '?!',
                    created: Math.floor(Date.now() / 1000),
                  },
                ];
              });

              if ('creditDelta' in statusData) {
                setCreditDelta(statusData.creditDelta!);
              }

              setIsSendingMessage(false);
              setIsTyping(false);
              setProcessingMessage('');
              setCreditError(null); // Reset credit error on completion
              break;
            }
            case 'ERROR':
            case 'ERROR_NOT_FOUND': {
              // Remove loading message on error
              setChatMessages((prev) => prev.slice(0, -1));

              setIsSendingMessage(false);
              setIsTyping(false);
              if (statusData.status === 'ERROR_NOT_FOUND') {
                toast({
                  title: 'Error',
                  description: 'Chat not found. Please try again.',
                  variant: 'destructive',
                });
              } else {
                setProcessingMessage(statusData.errorDetails || 'An error occurred during the operation.');
                // Clear error message after 10 seconds
                setTimeout(() => {
                  setProcessingMessage('');
                }, 10000);
              }
              break;
            }
            case 'ERROR_LACK_CREDIT': {
              // Remove loading message on error
              setChatMessages((prev) => prev.slice(0, -1));

              setIsSendingMessage(false);
              setIsTyping(false);
              setCreditError(
                `You've exceeded your credit limit. Used: ${statusData.creditsConsumed}, Limit: ${statusData.creditLimit}`
              );
              break;
            }
            case 'QUEUED':
            case 'IN_PROGRESS': {
              attempts++;
              setTimeout(poll, POLLING_INTERVAL);
              break;
            }
          }
        } catch (error) {
          console.error('Error polling status:', error);
          setIsSendingMessage(false);
          setIsTyping(false);
          setProcessingMessage('Error checking operation status. Please try again.');
          // Clear error message after 10 seconds
          setTimeout(() => {
            setProcessingMessage('');
          }, 10000);
        }
      };

      poll();
    },
    [apiCall, setSubscriptionSpend, setSubscriptionLimit, toast, id]
  );

  const handleSendMessage = useCallback(async () => {
    if (!currentMessage.trim()) return;

    setIsSendingMessage(true);
    setProcessingMessage('');

    const messageToSend = currentMessage.trim();

    // Append the user's message to the chat
    setChatMessages((prev) => [
      ...(prev ?? []),
      {
        user: true,
        msg: messageToSend,
        created: Math.floor(Date.now() / 1000),
      },
    ]);
    setCurrentMessage('');

    // Add a loading message that will be replaced by the actual response
    setChatMessages((prev) => [
      ...prev,
      {
        user: false,
        msg: '',
        isLoading: true,
        created: Math.floor(Date.now() / 1000),
      },
    ]);

    try {
      // Determine if we should use polling based on message count
      const usePolling = chatMessages.length >= MESSAGE_THRESHOLD;

      const response = await apiCall<StatusResponseWithId | ReferenceResponse>('/ai/chat', 'POST', {
        id, // Send id if we have one, otherwise it will be null/undefined
        message: messageToSend,
        offline: usePolling,
        toolbox,
      });

      // Check if we got a synchronous response (status is COMPLETE)
      if (!usePolling) {
        const r = response as StatusResponseWithId;
        // If this is the first message and we get a new ID back, store it
        if (!id && r.id) {
          setId(r.id);
        }
        if (r.status === 'COMPLETE') {
          // Handle synchronous response
          if (r.creditsConsumed !== undefined && r.creditLimit !== undefined) {
            setSubscriptionSpend(r.creditsConsumed);
            setSubscriptionLimit(r.creditLimit);
          }

          // Replace loading message with actual AI response
          setChatMessages((prev) => {
            const allButLast = prev.slice(0, -1);
            return [
              ...allButLast,
              {
                user: false,
                msg: r.aiResponse || '?!',
                created: Math.floor(Date.now() / 1000),
              },
            ];
          });

          // Update credit delta if available
          if ('creditDelta' in r) {
            setCreditDelta(r.creditDelta!);
          }

          setIsSendingMessage(false);
          setProcessingMessage('');
          setCreditError(null); // Reset credit error on completion
        }
      } else {
        // For polling we already added a loading message above, so just start polling
        pollChatStatus((response as ReferenceResponse).reference);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      // Remove loading message on error
      setChatMessages((prev) => prev.slice(0, -1));

      setIsSendingMessage(false);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    }
  }, [
    id,
    currentMessage,
    chatMessages.length,
    apiCall,
    setSubscriptionSpend,
    setSubscriptionLimit,
    pollChatStatus,
    toast,
    toolbox,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedPrompt = (prompt: { label: string; prompt: string; toolbox?: string }) => {
    setCurrentMessage(prompt.prompt);

    if (prompt.toolbox) {
      setToolbox(prompt.toolbox);

      // Update URL to include toolbox parameter without triggering a navigation
      if (!id) {
        const url = new URL(window.location.href);
        url.searchParams.set('toolbox', prompt.toolbox);
        window.history.replaceState({}, '', url.toString());
      }
    }

    // Focus the textarea after selecting a prompt
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col h-[90vh] bg-background border rounded-lg shadow-sm overflow-hidden">
        {/* Chat Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <Bot className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">AI Assistant</h2>
              <p className="text-xs text-muted-foreground">
                Powered by Creative AI Writer
                {toolbox !== 'research' && (
                  <span
                    id="toolbox-label"
                    className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                  >
                    {getToolboxDisplayName(toolbox)}
                  </span>
                )}
              </p>
            </div>
          </div>
          {creditDelta !== null && (
            <div className="text-xs text-muted-foreground flex items-center">
              <Info className="h-3 w-3 mr-1" />
              <span>Credits: ~{creditDelta.toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* Chat Messages Area */}
        <div className="flex-grow overflow-hidden flex flex-col relative">
          {creditError && (
            <div
              className="sticky top-0 z-10 bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-md m-4"
              role="alert"
            >
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <div>
                  <strong className="font-bold">Credit limit exceeded!</strong>
                  <span className="block sm:inline ml-1">{creditError}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex-grow overflow-y-auto p-4 space-y-4">
            {/* Welcome message for new chats */}
            {chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full space-y-6 py-12">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                  <Sparkles className="h-8 w-8 text-primary" />
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-semibold">Welcome to AI Chat</h3>
                  <p className="text-muted-foreground max-w-md">
                    Ask me anything! I can help with creative writing, answer questions, or just chat.
                  </p>
                </div>

                {/* Suggested prompts */}
                <div className="grid grid-cols-1 gap-3 w-full max-w-2xl">
                  {SUGGESTED_PROMPTS.map((promptItem, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="flex flex-col items-start text-left h-auto py-3 px-4 space-y-1 hover:bg-primary/5 w-full"
                      onClick={() => handleSuggestedPrompt(promptItem)}
                    >
                      <span className="font-medium text-primary w-full">{promptItem.label}</span>
                      <span className="text-xs text-muted-foreground w-full whitespace-normal">
                        {promptItem.prompt}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Custom message rendering */}
                {chatMessages.map((message, index) => (
                  <div
                    key={index}
                    className={cn('flex items-start gap-3', message.user ? 'justify-end' : 'justify-start')}
                  >
                    {!message.user && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-5 w-5 text-primary" />
                      </div>
                    )}

                    <div
                      className={cn(
                        'rounded-lg p-3 max-w-[80%]',
                        message.user
                          ? 'bg-primary text-primary-foreground rounded-tr-none'
                          : 'bg-muted text-foreground rounded-tl-none'
                      )}
                    >
                      {message.isLoading ? (
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-2 h-2 rounded-full bg-current animate-bounce"
                            style={{ animationDelay: '0ms' }}
                          ></div>
                          <div
                            className="w-2 h-2 rounded-full bg-current animate-bounce"
                            style={{ animationDelay: '300ms' }}
                          ></div>
                          <div
                            className="w-2 h-2 rounded-full bg-current animate-bounce"
                            style={{ animationDelay: '600ms' }}
                          ></div>
                        </div>
                      ) : message.user ? (
                        <p className="text-sm whitespace-pre-wrap">{message.msg}</p>
                      ) : (
                        <div className="text-sm prose dark:prose-invert max-w-none">
                          <ReactMarkdown>{message.msg}</ReactMarkdown>
                        </div>
                      )}
                    </div>

                    {message.user && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <User className="h-5 w-5 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-primary" />
                    </div>
                    <div className="rounded-lg p-3 bg-muted text-foreground rounded-tl-none">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-2 h-2 rounded-full bg-current animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        ></div>
                        <div
                          className="w-2 h-2 rounded-full bg-current animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        ></div>
                        <div
                          className="w-2 h-2 rounded-full bg-current animate-bounce"
                          style={{ animationDelay: '600ms' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                {processingMessage && (
                  <div className="flex items-center justify-center py-2 px-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-sm">{processingMessage}</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="border-t p-4">
          <div className="flex items-start space-x-2">
            <Textarea
              ref={textareaRef}
              placeholder="Type your message here..."
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSendingMessage || !!creditError}
              className="flex-grow resize-none"
              rows={4}
              maxLength={10000}
            />
            <Button
              onClick={handleSendMessage}
              disabled={isSendingMessage || !currentMessage.trim() || !!creditError}
              size="icon"
              className="h-12 w-12 rounded-full flex-shrink-0"
            >
              {isSendingMessage ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              <span className="sr-only">Send message</span>
            </Button>
          </div>

          <div className="mt-2 text-xs text-muted-foreground flex items-center justify-between">
            <div className="flex items-center">
              <span>{currentMessage.length}/10000 characters</span>
            </div>
            <div className="flex items-center">
              <span className="mr-2">Press Shift+Enter for a new line</span>
              {creditDelta !== null && (
                <>
                  <span className="mx-2">•</span>
                  <Info className="h-3 w-3 mr-1" />
                  <span>Last credit: ~{creditDelta.toFixed(2)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
